/**
 * The engine's labels on the dashboard's Activity pages.
 *
 * Each morning's `day` result carries `screens`: for every computer, every
 * screen (an app, a window title and the site when the browser recorded it)
 * the rules labelled that day, with its label (the readout function and
 * step, or Personal, Not known, Work not yet named). Loaded here for the
 * days a page shows, they become the categories the Activity pages roll time
 * up by, in place of this app's own category list, so what the Devices
 * pages show is what the readout counts.
 *
 * A label belongs to one computer, one day and one screen: a lookup carries
 * all three, and a screen the engine didn't label on that computer that day
 * is "Not yet named". Nothing is borrowed from another computer, another
 * day, or a page with the same title on another site.
 *
 * Results open only with the client's key and the verified engine's signing
 * key, exactly as on the Zunox tab; without either there are no labels and
 * the pages say so.
 */
import moment from 'moment';
import * as hubClient from '~/util/hubClient';
import { engineChoices } from '~/util/keyVault';
import { listItems, openItem } from '~/util/zunoxEngine';
import { NOT_YET_NAMED } from '~/util/appCatalog';
import { NO_TITLE, PRIVATE_WINDOW_TITLE } from '~/util/intervalPayload';
import { cleanText } from '~/util/cleanText';
export { cleanText };
/** The engine hasn't looked at that day yet (it looks the next morning, or
 * when asked again on the Zunox tab): nothing is known about any screen. */
export const NOT_LOOKED_AT = ['Not looked at yet'];
/** The computer isn't on the client's staff list, so the engine leaves its
 * records out. */
export const NOT_ON_STAFF_LIST = ['Not on the staff list'];
/** The engine looked at the day, but its result wouldn't open here (a wrong
 * key, a result that failed its checks): nothing is known about any screen,
 * and it isn't the same as a day not looked at. */
export const COULD_NOT_OPEN = ["Couldn't be opened"];
export const DEFAULT_CLOCK = { timeZone: null, dayStartHour: 4 };
/** The engine account chosen for a client on the Zunox tab (Set up),
 * remembered in this browser under this key. */
export const ENGINE_CHOICE = 'zunox-engine-account';
export function chosenEngine(orgId) {
    try {
        return localStorage.getItem(`${ENGINE_CHOICE}:${orgId}`);
    }
    catch {
        return null;
    }
}
/** What opens the engine's results for a client, or null (no key, no
 * engine chosen, or the chosen one not verified). */
export async function readerFor(orgId, secret, userId) {
    if (!secret)
        return null;
    const chosen = chosenEngine(orgId);
    if (!chosen)
        return null;
    const recipients = await hubClient.listKeyRecipients(orgId);
    const engine = (await engineChoices(recipients, userId)).find(e => e.userId === chosen && e.verified && e.signingKey);
    return engine?.signingKey ? { orgId, secret, engineSigningKey: engine.signingKey } : null;
}
/** Opened results, by client, by the client's key and the engine key they
 * were opened under, and by item: a result opened for one client under one
 * pair of keys is never handed back under another, however the item id is
 * replayed, and a wrong key finds nothing here and has to open the result
 * itself (which fails). Lives as long as the unlock does: locking, signing
 * out or unlocking with another key drops a client's entries. Bounded: the
 * oldest entries go once there are more than a few months of days. */
const opened = new Map();
const OPENED_KEPT = 120;
function cacheKey(reader, itemId) {
    return [reader.orgId, fingerprint(reader.secret), reader.engineSigningKey, itemId].join('\u0000');
}
/** A short, non-reversible stand-in for a key, so the key itself isn't
 * copied into every cache entry. */
function fingerprint(secret) {
    let h1 = 0x811c9dc5;
    let h2 = 0x01000193;
    for (let i = 0; i < secret.length; i++) {
        const c = secret.charCodeAt(i);
        h1 = Math.imul(h1 ^ c, 0x01000193);
        h2 = Math.imul(h2 + c, 0x9e3779b1) ^ (h2 >>> 15);
    }
    return `${(h1 >>> 0).toString(16)}${(h2 >>> 0).toString(16)}${secret.length}`;
}
function slim(o) {
    const p = o.payload;
    // A result from before the engine sent its screens says nothing about
    // any screen: the day reads as not looked at, never as an empty staff list.
    if (!p.screens)
        return null;
    const screens = p.screens;
    return {
        madeAt: Date.parse(o.madeAt),
        screens,
        // The staff list when the result carries it; else unknown (not empty),
        // and the lookup counts the computers the engine labelled anything for.
        staff: p.computers?.names
            ? Array.from(new Set([
                ...p.computers.names,
                ...Object.keys(screens),
                ...(p.computers.sent_nothing ?? []),
            ]))
            : null,
        ids: pulledIds(p.computers?.ids),
        clock: p.time_zone ? { timeZone: p.time_zone, dayStartHour: p.day_start_hour ?? 4 } : null,
    };
}
function pulledIds(value) {
    if (!value || typeof value !== 'object')
        return null;
    const out = {};
    for (const [name, id] of Object.entries(value)) {
        if (typeof id === 'string' && id)
            out[name] = id;
    }
    // None at all (no pull report beside the records): matched by name.
    return Object.keys(out).length ? out : null;
}
/** The devices behind a name the engine used on a day: the Hub id the pull
 * named, when the result carries one for it (a replacement laptop with the
 * old one's hostname is then never given its labels); else every device the
 * Hub calls by that name, except those the pull named under another name. */
function devicesFor(name, pulled, byName) {
    if (pulled?.[name])
        return [pulled[name]];
    const claimed = new Set(Object.values(pulled ?? {}));
    return Array.from(byName.get(name.trim().toLowerCase()) ?? []).filter(id => !claimed.has(id));
}
/** How many times what was opened has been forgotten, for every client and
 * for each: a load that began before a forget (a lock, a sign-out, a change
 * of key) sees the count move and comes to nothing, so a listing or an open
 * still in flight at sign-out can never put a result back. */
let everyone = 0;
const forgotten = new Map();
function generation(orgId) {
    return `${everyone}:${forgotten.get(orgId) ?? 0}`;
}
/** Where the forgetting stands for a client: taken before anything a load
 * awaits (the key, the engine's account), and handed to loadEngineScreens,
 * so a forget at any point from then on ends the load. */
export function openedGeneration(orgId) {
    return generation(orgId);
}
/** Drops what was opened for one client (on lock, sign-out or a change of
 * key), or for every client when none is named. */
export function forgetOpened(orgId) {
    if (orgId === undefined) {
        everyone += 1;
        opened.clear();
        return;
    }
    forgotten.set(orgId, (forgotten.get(orgId) ?? 0) + 1);
    const prefix = `${orgId}\u0000`;
    for (const key of Array.from(opened.keys()))
        if (key.startsWith(prefix))
            opened.delete(key);
}
/** Thrown by a load whose client was forgotten while it ran. */
export class ForgottenWhileLoading extends Error {
    constructor() {
        super('forgotten while loading');
        this.name = 'ForgottenWhileLoading';
    }
}
/** Pages of the listing asked for at most, newest first. */
const LISTING_PAGE = 200;
const LISTING_PAGES = 12;
/** The client's day results whose label is one of `wanted`, however many
 * results the client has: the Hub lists newest first, a page at a time, and
 * a day's result is made no earlier than that day, so the walk stops once
 * it has passed everything made before the earliest day wanted. */
async function listDayItems(orgId, wanted) {
    const earliest = Array.from(wanted).sort()[0];
    if (!earliest)
        return [];
    // Made before the earliest wanted day started, with a day of grace for
    // the clock the result was made on.
    const floor = Date.parse(`${shiftDate(earliest, -1)}T00:00:00Z`);
    const found = [];
    let before = null;
    for (let page = 0; page < LISTING_PAGES; page++) {
        const items = await listItems(orgId, ['day'], LISTING_PAGE, before);
        for (const i of items)
            if (wanted.has(i.label))
                found.push(i);
        const last = items[items.length - 1];
        if (items.length < LISTING_PAGE || !last || Date.parse(last.made_at) < floor)
            break;
        before = last.made_at;
    }
    return found;
}
/** The screens the engine labelled on each of `days` (YYYY-MM-DD, the
 * client's days), kept apart by day. A day the engine has looked at more
 * than once (a look-again) yields its newest result, by when the engine
 * made it (from inside its signature), whatever order the Hub lists them
 * in. Days the engine hasn't looked at are simply absent. A day none of
 * whose results will open (wrong key, tampered, offline) is counted in
 * `failed` (as a day, among `days` asked for) and left out rather than
 * taking the other days with it. */
export async function loadEngineScreens(reader, days, since) {
    const wanted = new Set(days);
    const started = since ?? generation(reader.orgId);
    const stillWanted = () => generation(reader.orgId) === started;
    // Forgotten before it began (between the caller taking `since` and now).
    if (!stillWanted())
        throw new ForgottenWhileLoading();
    const items = await listDayItems(reader.orgId, wanted);
    // Locked, signed out or re-keyed while the listing was out: open nothing.
    if (!stillWanted())
        throw new ForgottenWhileLoading();
    const results = await Promise.allSettled(items.map(async (i) => {
        const key = cacheKey(reader, i.id);
        let p = opened.get(key);
        if (!p) {
            const mine = openItem(reader, i.id, {
                kind: 'day',
                label: i.label,
            }).then(slim);
            p = mine;
            // Only this entry is dropped on failure, or when the client was
            // forgotten while it was opening; never a newer entry under the key.
            const drop = () => {
                if (opened.get(key) === mine)
                    opened.delete(key);
            };
            mine.then(() => {
                if (!stillWanted())
                    drop();
            }, drop);
            opened.set(key, mine);
            while (opened.size > OPENED_KEPT) {
                const oldest = opened.keys().next().value;
                if (oldest === undefined)
                    break;
                opened.delete(oldest);
            }
        }
        try {
            const o = await p;
            return o ? { day: i.label, ...o } : { day: i.label, noScreens: true };
        }
        catch (err) {
            throw Object.assign(new Error(err instanceof Error ? err.message : String(err)), {
                day: i.label,
            });
        }
    }));
    // Forgotten while opening: nothing opened leaves here.
    if (!stillWanted())
        throw new ForgottenWhileLoading();
    const newest = new Map();
    const failedDays = new Set();
    const withoutScreens = new Set();
    for (const r of results) {
        if (r.status === 'rejected') {
            failedDays.add(String(r.reason.day ?? ''));
            continue;
        }
        if (!r.value)
            continue;
        if ('noScreens' in r.value) {
            withoutScreens.add(r.value.day);
            continue;
        }
        const have = newest.get(r.value.day);
        if (!have || r.value.madeAt > have.madeAt)
            newest.set(r.value.day, r.value);
    }
    // A day counts as failed only when none of its results opened: an older
    // look-again that won't open doesn't matter once a newer one did.
    for (const day of newest.keys()) {
        failedDays.delete(day);
        withoutScreens.delete(day);
    }
    for (const day of withoutScreens)
        failedDays.delete(day);
    const failed = failedDays.size;
    const byDay = {};
    const staffByDay = {};
    const idsByDay = {};
    let clock = DEFAULT_CLOCK;
    let clockFrom = -Infinity;
    for (const [day, o] of newest) {
        byDay[day] = o.screens;
        if (o.staff)
            staffByDay[day] = o.staff;
        if (o.ids)
            idsByDay[day] = o.ids;
        // The clock as the engine most recently kept it.
        if (o.clock && o.madeAt > clockFrom) {
            clock = o.clock;
            clockFrom = o.madeAt;
        }
    }
    return {
        byDay,
        staffByDay,
        idsByDay,
        days: Object.keys(byDay).sort(),
        clock,
        failed,
        failedDays: Array.from(failedDays).sort(),
        withoutScreens: Array.from(withoutScreens).sort(),
    };
}
const dayFormats = new Map();
/** Moves a calendar date (YYYY-MM-DD) by `days`, with no clock involved. */
export function shiftDate(date, days) {
    const [y, m, d] = date.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
/** The client's day (YYYY-MM-DD) a moment belongs to, by the engine's
 * clock: the date on the client's wall clock, or the day before when the
 * wall clock reads earlier than the day-start hour. The boundary is applied
 * in local time, never by subtracting hours from the instant, so it holds
 * across daylight-saving changes (the morning the clocks go forward, 04:30
 * is still the new day). Falls back to this browser's zone when the
 * client's is unknown or unknown to this browser. */
export function clientDayOf(timestamp, clock) {
    if (!timestamp)
        return null;
    const at = new Date(timestamp);
    if (Number.isNaN(at.getTime()))
        return null;
    if (clock.timeZone) {
        let format = dayFormats.get(clock.timeZone);
        if (format === undefined) {
            try {
                format = new Intl.DateTimeFormat('en-CA', {
                    timeZone: clock.timeZone,
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    hourCycle: 'h23',
                });
            }
            catch {
                format = null;
            }
            dayFormats.set(clock.timeZone, format);
        }
        if (format) {
            const parts = format.formatToParts(at);
            const part = (type) => parts.find(p => p.type === type)?.value ?? '';
            const date = `${part('year')}-${part('month')}-${part('day')}`;
            const hourPart = part('hour');
            // A browser that gives no hour, or a 12-hour one despite being asked
            // for 24, can't place the boundary: fall back rather than shift wrongly.
            if (/^\d{4}-\d{2}-\d{2}$/.test(date) && /^\d{1,2}$/.test(hourPart) && !part('dayPeriod')) {
                const hour = Number(hourPart) % 24;
                return hour < clock.dayStartHour ? shiftDate(date, -1) : date;
            }
        }
    }
    const local = moment(at);
    const date = local.format('YYYY-MM-DD');
    return local.hour() < clock.dayStartHour ? shiftDate(date, -1) : date;
}
/** A screen's label on one computer on one day. Nothing is borrowed across
 * computers, days or sites; an event without a computer or a time gets no
 * label, since there is no telling whose day it belongs to. A day the
 * engine hasn't looked at, and a computer the engine never named (not on
 * the staff list), each get a label saying so, so neither reads as time the
 * rules couldn't name. */
/** The client's days (YYYY-MM-DD) from `from` to `to` inclusive, by date
 * alone, so a 23- or 25-hour day counts once. */
export function daysBetween(from, to) {
    const out = [];
    for (let d = from; d <= to && out.length < 400; d = shiftDate(d, 1))
        out.push(d);
    return out;
}
export function engineLookup(byDay, devices, clock = DEFAULT_CLOCK, staffByDay = {}, failedDays = [], idsByDay = {}) {
    const failed = new Set(failedDays);
    // Computer names as the Hub compares them against the staff list:
    // trimmed and lower-cased, so MIKES-PC and Mikes-PC are one computer.
    const nameKey = (n) => n.trim().toLowerCase();
    // A name can belong to more than one Hub device (a re-enrolled laptop
    // keeps its hostname under a new id), and the engine knows only the name,
    // so its labels apply to every device of that name.
    const idsOf = new Map();
    for (const d of devices) {
        for (const n of d.names) {
            if (!n || !n.trim())
                continue;
            const key = nameKey(n);
            const ids = idsOf.get(key) ?? new Set();
            ids.add(d.id);
            idsOf.set(key, ids);
        }
    }
    const labels = new Map();
    // Which computers the engine counted on each day: the staff list when the
    // result says, else those it labelled anything for.
    const staff = new Map();
    for (const [day, screens] of Object.entries(byDay)) {
        // The devices behind each name the engine used: the Hub ids the pull
        // named, when the result carries them (a replacement laptop with the
        // old one's hostname is then never given its labels), else every device
        // the Hub calls by that name.
        const pulled = idsByDay[day];
        const devicesOf = (name) => devicesFor(name, pulled, idsOf);
        const ids = new Set();
        for (const name of staffByDay[day] ?? Object.keys(screens)) {
            for (const id of devicesOf(name))
                ids.add(id);
        }
        staff.set(day, ids);
        for (const [name, rows] of Object.entries(screens)) {
            const deviceIds = Array.from(devicesOf(name));
            if (!deviceIds.length)
                continue;
            for (const row of rows) {
                const [app, title, site, ...label] = row;
                if (!label.length || typeof app !== 'string')
                    continue;
                const key = [cleanText(app), cleanText(title), cleanText(site).toLowerCase()].join('\u0000');
                for (const id of deviceIds)
                    labels.set(`${id}\u0000${day}\u0000${key}`, label);
            }
        }
    }
    return (app, title, context) => {
        const day = clientDayOf(context.timestamp, clock);
        if (!context.device || !day)
            return null;
        const counted = staff.get(day);
        if (!counted)
            return failed.has(day) ? COULD_NOT_OPEN : NOT_LOOKED_AT;
        if (!counted.has(context.device))
            return NOT_ON_STAFF_LIST;
        const site = cleanText(context.domain).toLowerCase();
        return (labels.get([context.device, day, cleanText(app), cleanText(title), site].join('\u0000')) ??
            null);
    };
}
/** The lookup applied to a classified event: app and title from its data,
 * the computer, time and site from the rest of it. */
export function labelFor(lookup, event) {
    // The engine keys screens by the app as the tracker recorded it (a frame
    // host is shown under its window's name here) and knows a withheld title
    // as no title.
    const app = cleanText(event.data.recorded_app ?? event.data.app);
    const context = {
        device: typeof event.data.device === 'string' ? event.data.device : undefined,
        timestamp: event.timestamp,
        domain: typeof event.data.domain === 'string' ? event.data.domain : null,
    };
    const title = event.data.title;
    const bare = title === PRIVATE_WINDOW_TITLE || title === NO_TITLE ? '' : cleanText(title);
    return (lookup(app, bare, context) ??
        // An app with no name of its own goes by its bundle id, here and in the
        // engine since 31 October; a day the engine labelled before then keyed
        // it as no name at all.
        // Only for a titled window: an untitled one could be any nameless app's.
        (app && bare && app === cleanText(event.data.bundle_id) ? lookup('', bare, context) : null) ??
        NOT_YET_NAMED);
}
/** Colours by readout function, so a function's steps share a hue. */
const FUNCTION_COLOURS = [
    '#0e7c78',
    '#2c6fb7',
    '#8a5cb8',
    '#c27c2c',
    '#b23a5a',
    '#3f8f4e',
    '#5f7180',
];
const FIXED = {
    Personal: '#9aa5b1',
    'Not known': '#c9d0d6',
    'Not yet named': '#e0e4e8',
    'Not looked at yet': '#eef0f2',
    'Not on the staff list': '#eef0f2',
    "Couldn't be opened": '#f3d9d9',
    Private: '#d7dbe0',
    Excluded: '#d7dbe0',
    // Work the rules saw but gave no step to: a fixed grey, so it never takes
    // a function's hue and the functions' hues don't shift with it.
    Work: '#b8c0c8',
};
/** The categories a page rolls time up by when the engine's labels are in
 * hand: one per label the engine used on the computers in view, a function
 * above its steps, plus the lookup itself (a category of type `engine`,
 * which `classifyDeviceEvents` recognises). */
export function engineCategories(byDay, devices, clock = DEFAULT_CLOCK, staffByDay = {}, failedDays = [], firstDays = null, idsByDay = {}) {
    const lookup = engineLookup(byDay, devices, clock, staffByDay, failedDays, idsByDay);
    const first = firstDays ? new Set(firstDays) : null;
    const shownFunctions = new Set();
    // Names as the Hub compares them: trimmed and lower-cased (see engineLookup).
    const inView = new Set(devices.map(d => d.id));
    const viewByName = new Map();
    for (const d of devices)
        for (const n of d.names) {
            if (!n || !n.trim())
                continue;
            const key = n.trim().toLowerCase();
            viewByName.set(key, (viewByName.get(key) ?? new Set()).add(d.id));
        }
    const names = new Map();
    // Functions in a fixed order (sorted), so a function keeps its hue from
    // one period to the next whatever order the results list them in.
    const functions = new Set();
    for (const [day, screens] of Object.entries(byDay)) {
        // A computer in view by its Hub id when the result names ids (a name
        // with an id after it is no hostname), else by name.
        const pulled = idsByDay[day];
        for (const [name, rows] of Object.entries(screens)) {
            if (!devicesFor(name, pulled, viewByName).some(id => inView.has(id)))
                continue;
            for (const row of rows) {
                const label = row.slice(3);
                if (!label.length)
                    continue;
                names.set(label.join('>'), label);
                if (label.length > 1 && !FIXED[label[0]]) {
                    functions.add(label[0]);
                    if (!first || first.has(day))
                        shownFunctions.add(label[0]);
                }
            }
        }
    }
    // `firstDays` (the days shown): their functions take the first hues, so
    // the previous period's labels arriving later never recolour what's drawn.
    const ranked = [
        ...Array.from(shownFunctions).sort(),
        ...Array.from(functions)
            .filter(f => !shownFunctions.has(f))
            .sort(),
    ];
    const hueOf = new Map(ranked.map((f, i) => [f, i]));
    for (const fixed of [
        NOT_YET_NAMED,
        NOT_LOOKED_AT,
        NOT_ON_STAFF_LIST,
        ...(failedDays.length ? [COULD_NOT_OPEN] : []),
    ])
        names.set(fixed.join('>'), fixed);
    const out = [{ name: ['Engine'], rule: { type: 'engine', lookup } }];
    for (const label of names.values()) {
        const colour = FIXED[label[0]] ?? FUNCTION_COLOURS[(hueOf.get(label[0]) ?? 0) % FUNCTION_COLOURS.length];
        const have = (name) => out.some(c => c.name.length === name.length && c.name.every((n, i) => n === name[i]));
        if (label.length > 1 && !have([label[0]])) {
            out.push({ name: [label[0]], rule: { type: 'none' }, data: { color: colour } });
        }
        if (!have(label))
            out.push({ name: label, rule: { type: 'none' }, data: { color: colour } });
    }
    return out;
}
/** Colour for an engine label, by the label or its function. */
export function engineColour(categories) {
    return cat => {
        const key = cat.join('>');
        const found = categories.find(c => c.name.join('>') === key) ?? categories.find(c => c.name[0] === cat[0]);
        return found?.data?.color ?? FIXED['Not yet named'];
    };
}
/** A step of the client's work scores one, everything else nothing, so the
 * score card reads "named work". */
export function engineScore(cat) {
    // Time the engine says nothing about (a day it hasn't looked at, a
    // computer not on the staff list, a screen its result doesn't cover)
    // isn't scored at all: NaN leaves it out of the score and the percentage.
    const key = cat.join('>');
    if ([NOT_LOOKED_AT, NOT_ON_STAFF_LIST, NOT_YET_NAMED, COULD_NOT_OPEN].some(l => l.join('>') === key))
        return NaN;
    // Personal time counts against; a named step (whether or not its function
    // is named: the engine files a step with no function under "Work") counts
    // for; work the rules couldn't name, and "not known", count neither way.
    if (cat[0] === 'Personal')
        return -1;
    if (cat.length < 2)
        return 0;
    return cat[0] === 'Work' && cat[1] === 'not yet named' ? 0 : 1;
}
/** The options for the category filter: every label, with its function first. */
export function engineOptions(categories) {
    return categories
        .filter(c => c.rule.type !== 'engine')
        .map(c => ({ text: c.name.join(' > '), value: c.name }));
}
